import { useEffect, useMemo, useState } from "react";
import { ResourceLocation } from "../../../Constants/resourceLocations";
import { IKnowledgeArticleModel, KnowledgeArticleService } from "../../../Services/KnowledgeArticleService";
import { KNOWLEDGE_ARTICLE_RELATION_OVERVIEW } from "../../../Pages/KnowledgeBase/knowledgeArticleRelationConstants";

export interface LinkedKnowledgeArticlesTarget {
    location: ResourceLocation;
    /** Left out: the module's overview. */
    locationIdentifier?: string;
}

export interface LinkedKnowledgeArticlesTargetProps {
    location?: ResourceLocation;
    locationIdentifier?: string;
    locations?: readonly LinkedKnowledgeArticlesTarget[];
    /** Articles the page already fetched for these targets: nothing is fetched again. A page that shows both
     *  the info button and the in-page list loads once and hands the result to both. */
    articles?: IKnowledgeArticleModel[];
}

/** The articles linked to a page's overview or item(s), each carrying its relation's display mode and sections.
 *  Empty while loading and on failure: help content never blocks or breaks the page it sits on. */
export const useLinkedKnowledgeArticles = ({
    location,
    locationIdentifier,
    locations,
    articles: providedArticles,
}: LinkedKnowledgeArticlesTargetProps): IKnowledgeArticleModel[] => {
    const knowledgeArticleService = useMemo(() => new KnowledgeArticleService(), []);
    const [articles, setArticles] = useState<IKnowledgeArticleModel[]>([]);
    const provided = providedArticles !== undefined;

    const targets = useMemo(() => {
        const source = locations?.length
            ? locations
            : location
                ? [{ location, locationIdentifier }]
                : [];
        return source.map((target) => ({
            location: target.location,
            locationIdentifier: target.locationIdentifier?.trim() || KNOWLEDGE_ARTICLE_RELATION_OVERVIEW,
        }));
    }, [location, locationIdentifier, locations]);

    const targetKey = targets.map((target) => `${target.location}:${target.locationIdentifier}`).join("|");

    useEffect(() => {
        if (provided || targets.length === 0) {
            setArticles([]);
            return;
        }
        let cancelled = false;
        knowledgeArticleService
            .fetchByLocations(targets)
            .then((nextArticles) => {
                if (!cancelled) {
                    setArticles(nextArticles);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setArticles([]);
                }
            });
        return () => {
            cancelled = true;
        };
        // targetKey captures location + identifier; `targets` is rebuilt when that key changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetKey, provided]);

    return providedArticles ?? articles;
};
